import React, { useState } from 'react';

import dayjs from 'dayjs';
import styled from 'styled-components';

import { getFilename } from 'utils/helpers/files';

import BrochureAndDocsThumbnails from './BrochureAndDocsThumbnails';

const DESCRIPTION_LIMIT = 170;

const Container = styled.div`
  margin-top: 15px;
  display: flex;
  flex-direction: column;
  gap: 13px;
`;

const Row = styled.div`
  display: flex;
  gap: 16px;
`;

const Thumb = styled.div`
  flex-shrink: 0;
`;

const Body = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
`;

const TopRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 12px;
`;

const Title = styled.a`
  font-size: 14px;
  font-family: var(--font-regular);
  color: var(--dark-blue);

  &:hover {
    text-decoration: underline;
  }
`;

const FileDate = styled.span`
  flex-shrink: 0;
  font-size: 12px;
  color: var(--text-secondary);
`;

const FileLine = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 12px;
  color: var(--text-secondary);
`;

const DownloadLink = styled.a`
  color: var(--dark-blue);
  cursor: pointer;
`;

const downloadFile = async (url: string, fileName: string) => {
  try {
    const response = await fetch(url);
    const blob = await response.blob();
    const objectURL = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.setAttribute('href', objectURL);
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(objectURL);
  } catch (error) {
    window.open(url, '_blank', 'noopener,noreferrer');
  }
};

const Description = styled.p`
  margin: 5px 0 0 0;
  font-size: 13px;
  line-height: 1.5;
  color: var(--text-secondary);
`;

const Toggle = styled.span`
  color: var(--dark-blue);
  cursor: pointer;
`;

const DocumentDescription = ({ text }: { text: string }) => {
  const [expanded, setExpanded] = useState(false);
  const isLong = text.length > DESCRIPTION_LIMIT;
  const visibleText = !isLong || expanded ? text : `${text.slice(0, DESCRIPTION_LIMIT).trimEnd()}… `;

  return (
    <Description>
      {visibleText}
      {isLong && <Toggle onClick={() => setExpanded(!expanded)}>{expanded ? ' Show less' : 'Show more'}</Toggle>}
    </Description>
  );
};

interface Props {
  data: any;
  showDate?: boolean;
}

const BrochureAndDocsContent = ({ data, showDate }: Props) => (
  <Container>
    {data?.map((file: any) => {
      const fileName = file.file_name || getFilename(file.file_url);
      const categoryName = (file?.file_categories ?? []).map((category: any) => category.name).join(', ');
      return (
        <Row key={file.id}>
          <Thumb>
            <BrochureAndDocsThumbnails file={file} width={80} height={80} />
          </Thumb>
          <Body>
            <TopRow>
              <Title href={file.file_url} target="_blank" rel="noopener noreferrer">
                {categoryName || fileName}
              </Title>
              {showDate && file?.snapshot_date && <FileDate>{dayjs(file.snapshot_date).format('MMM YYYY')}</FileDate>}
            </TopRow>
            <FileLine>
              <span>{fileName}</span>
              <span>·</span>
              <DownloadLink
                onClick={(event) => {
                  event.preventDefault();
                  downloadFile(file.file_url, fileName);
                }}
              >
                Download
              </DownloadLink>
            </FileLine>
            {file?.analysis?.summary && <DocumentDescription text={file.analysis.summary} />}
          </Body>
        </Row>
      );
    })}
  </Container>
);

export default BrochureAndDocsContent;
